import React, { useEffect } from "react";
import styled from "styled-components";
import { BarChart2, ShoppingBag, Package, TrendingUp, Search } from "lucide-react";
import moment from "moment";
import { useTurnoversStore } from "./use-turnovers-store";

// ─── Layout ───────────────────────────────────────────────────────────────────

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  font-size: 21px;
  font-weight: 700;
  color: #202020;
  margin: 0;
`;

const TotalBadge = styled.span`
  font-size: 12px;
  opacity: 0.75;
`;

// ─── Stats Cards ──────────────────────────────────────────────────────────────

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`;

const IconWrap = styled.div<{ $bg: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${(p) => p.$bg};
  flex-shrink: 0;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #202020;
  line-height: 1.2;
`;

const CardLabel = styled.div`
  font-size: 12px;
  color: #666;
`;

// ─── Filters ──────────────────────────────────────────────────────────────────

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  max-width: 360px;
`;

const SearchIconWrap = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
  display: flex;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  &:focus {
    border-color: #1976d2;
  }
`;

// ─── Table ────────────────────────────────────────────────────────────────────

const TableWrap = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 12px;
  font-size: 13px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
`;

const MarketplaceBadge = styled.span<{ $mp: string }>`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  margin-left: 4px;
  background: ${(p) => (p.$mp === "wildberries" ? "#f3e8ff" : "#e3f2fd")};
  color: ${(p) => (p.$mp === "wildberries" ? "#7c3aed" : "#1976d2")};
`;

const StoreLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  & + & {
    margin-top: 4px;
  }
`;

const EmptyTd = styled.td`
  padding: 40px;
  text-align: center;
  color: #999;
  font-size: 14px;
`;

function fmt(n: number) {
  return Number(n).toLocaleString("ru-RU", { maximumFractionDigits: 0 }) + " ₽";
}

// ─── Component ────────────────────────────────────────────────────────────────

export const TurnoversPage: React.FC = () => {
  const { filteredItems, stats, query, loading, fetch, setQuery } = useTurnoversStore();

  useEffect(() => { fetch(); }, []);

  return (
    <PageContainer>
      <PageHeader>
        <BarChart2 size={24} color="#202020" />
        <PageTitle>
          Обороты
          <TotalBadge> ({filteredItems.length})</TotalBadge>
        </PageTitle>
      </PageHeader>

      {stats && (
        <StatsRow>
          <Card>
            <IconWrap $bg="#e3f2fd">
              <BarChart2 size={20} color="#1976d2" />
            </IconWrap>
            <CardContent>
              <CardValue>{stats.total_accounts}</CardValue>
              <CardLabel>Клиентов</CardLabel>
            </CardContent>
          </Card>
          <Card>
            <IconWrap $bg="#f3e8ff">
              <ShoppingBag size={20} color="#7c3aed" />
            </IconWrap>
            <CardContent>
              <CardValue>{fmt(stats.wb_total)}</CardValue>
              <CardLabel>Wildberries</CardLabel>
            </CardContent>
          </Card>
          <Card>
            <IconWrap $bg="#e3f2fd">
              <Package size={20} color="#1976d2" />
            </IconWrap>
            <CardContent>
              <CardValue>{fmt(stats.ozon_total)}</CardValue>
              <CardLabel>Ozon</CardLabel>
            </CardContent>
          </Card>
          <Card>
            <IconWrap $bg="#e8f5e9">
              <TrendingUp size={20} color="#2e7d32" />
            </IconWrap>
            <CardContent>
              <CardValue>{fmt(stats.grand_total)}</CardValue>
              <CardLabel>Итого за 30 дней</CardLabel>
            </CardContent>
          </Card>
        </StatsRow>
      )}

      <FiltersRow>
        <SearchWrap>
          <SearchIconWrap>
            <Search size={16} />
          </SearchIconWrap>
          <SearchInput
            type="text"
            placeholder="Поиск по email или логину..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </SearchWrap>
      </FiltersRow>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Клиент</Th>
              <Th>Дата регистрации</Th>
              <Th>Магазины / Оборот</Th>
              <Th>Итого за 30 дней</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><EmptyTd colSpan={4}>Загрузка...</EmptyTd></tr>
            )}
            {!loading && filteredItems.length === 0 && (
              <tr><EmptyTd colSpan={4}>Нет данных</EmptyTd></tr>
            )}
            {filteredItems.map((item) => (
              <tr key={item.account_id}>
                <Td>
                  <div style={{ fontWeight: 500 }}>
                    {item.public_login || item.email || "—"}
                  </div>
                  {item.public_login && item.email && (
                    <div style={{ fontSize: 11, color: "#999" }}>{item.email}</div>
                  )}
                </Td>
                <Td>{moment(item.created_at).format("DD.MM.YYYY HH:mm")}</Td>
                <Td>
                  {item.stores.length === 0 && <span style={{ color: "#ccc" }}>—</span>}
                  {item.stores.map((store) => (
                    <StoreLine key={store.connection_id}>
                      <span>{store.store_name || store.marketplace_store_id}</span>
                      <span style={{ color: "#999" }}>—</span>
                      <span style={{ fontWeight: 500 }}>{fmt(store.turnover)}</span>
                      <MarketplaceBadge $mp={store.marketplace}>
                        {store.marketplace === "wildberries" ? "WB" : "Ozon"}
                      </MarketplaceBadge>
                    </StoreLine>
                  ))}
                </Td>
                <Td style={{ fontWeight: 700, fontSize: 14 }}>
                  {fmt(item.total_turnover)}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
    </PageContainer>
  );
};
